import { createContext, useContext, type ReactNode } from "react";
import type { UseFormReturn } from "react-hook-form";
import type { useInvestorInfo } from "creso-core/hooks";
import type { Changes, Client } from "creso-core/types";
import type { AccountCenterFormValues } from "./form-schema";

export interface AccountFormContextValue {
  form: UseFormReturn<AccountCenterFormValues>;
  isEditing: boolean;
  isSaving: boolean;
  data: ReturnType<typeof useInvestorInfo>["data"];
  updateChangeSet: (changes: Changes) => Promise<void>;
  client: Client;
  /**
   * Called after a section writes to the change set (adding a bank account or
   * nominee saves immediately, unlike the plain fields). Hosts use it to
   * refresh route data — partners calls `router.invalidate()`.
   */
  onChangeSetUpdated?: () => void;
  /**
   * Called after a bank/nominee dialog commits. Partners opens its
   * "account-updates" review modal here; hosts without one can omit it.
   */
  onReviewChanges?: () => void;
}

const AccountFormContext = createContext<AccountFormContextValue | undefined>(
  undefined
);

export function AccountFormProvider({
  children,
  value,
}: {
  children: ReactNode;
  value: AccountFormContextValue;
}) {
  return (
    <AccountFormContext.Provider value={value}>
      {children}
    </AccountFormContext.Provider>
  );
}

export function useAccountForm() {
  const context = useContext(AccountFormContext);
  if (context === undefined) {
    throw new Error("useAccountForm must be used within AccountFormProvider");
  }
  return context;
}
